import React, { useEffect, useState } from "react";
import {
  Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell, Chip,
  IconButton, Tooltip, TablePagination, Box, TextField
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { FormsApi, fmtDateTime } from "../formsUtils";
import ResponseDetail from "../responses/ResponseDetail";

/** Ansiedade alta ganha destaque: é quem mais tende a querer conversar antes da cirurgia. */
const ANSIEDADE_ALTA = ["Muito ansioso", "Extremamente ansioso"];

/**
 * Possíveis clientes: quem pediu consulta com o anestesista ou quis saber os detalhes da
 * anestesia. Sai das respostas do formulário, com o contato de quem respondeu.
 */
export default function Interessados({ query }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [detail, setDetail] = useState({ open: false, responseId: null, formId: null });
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");

  // A busca é no servidor, porque a lista é paginada: esperar a digitação parar evita uma
  // consulta por tecla.
  useEffect(() => {
    const id = setTimeout(() => setTermo(busca.trim()), 400);
    return () => clearTimeout(id);
  }, [busca]);

  useEffect(() => {
    const params = [`page=${page + 1}`, `limit=${limit}`];
    if (query) params.push(query);
    if (termo) params.push(`q=${encodeURIComponent(termo)}`);
    FormsApi.getInteressados(`?${params.join("&")}`).then(
      (resp) => { setRows(resp?.data?.data || []); setTotal(resp?.data?.total || 0); },
      () => { setRows([]); setTotal(0); }
    );
  }, [query, page, limit, termo]);

  // Filtro novo recomeça a paginação: ficar na página 3 de uma lista que encolheu mostra vazio.
  useEffect(() => { setPage(0); }, [query, termo]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Typography variant="subtitle1">Interesse em consulta</Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        Quem pediu uma consulta com o anestesista ou quis saber os detalhes da anestesia.
      </Typography>

      <TextField
        size="small" sx={{ mt: 1, minWidth: 280 }}
        label="Buscar por nome, telefone ou CPF"
        value={busca} onChange={(e) => setBusca(e.target.value)}
      />

      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Paciente</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>Telefone</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", width: 150 }}>CPF</TableCell>
            <TableCell>O que marcou</TableCell>
            <TableCell sx={{ width: 170 }}>Ansiedade</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap", width: 170 }}>Respondeu em</TableCell>
            <TableCell align="right">Resposta</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.response_id} hover>
              <TableCell>{row.contact_name || "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{row.contact_phone || "—"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{row.external_ref || "—"}</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {/^gostaria/i.test(row.consulta || "") && <Chip size="small" color="success" label="Quer consulta" />}
                  {String(row.detalhes || "").toLowerCase() === "sim" && <Chip size="small" label="Quer detalhes" />}
                </Box>
              </TableCell>
              <TableCell>
                {row.ansiedade
                  ? <Chip size="small" color={ANSIEDADE_ALTA.includes(row.ansiedade) ? "warning" : "default"}
                      label={row.ansiedade} />
                  : "—"}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDateTime(row.submitted_at)}</TableCell>
              <TableCell align="right">
                <Tooltip title="Ver insumo">
                  <IconButton size="small"
                    onClick={() => setDetail({ open: true, responseId: row.response_id, formId: row.form_id })}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ color: "#999", py: 4 }}>
                Ninguém marcou interesse no período.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, nova) => setPage(nova)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      )}

      <ResponseDetail
        open={detail.open}
        responseId={detail.responseId}
        formId={detail.formId}
        onClose={() => setDetail({ open: false, responseId: null, formId: null })}
      />
    </Paper>
  );
}
